export type ErrorCode =
  | 'VALIDATION_ERROR' | 'NOT_FOUND' | 'REVISION_CONFLICT' | 'INVARIANT_CONFLICT'
  | 'STORAGE_QUOTA' | 'STORAGE_UNAVAILABLE' | 'TRANSACTION_ABORTED'
  | 'SCHEMA_UNSUPPORTED' | 'UPGRADE_BLOCKED' | 'CORRUPTION_DETECTED'
  | 'DATASET_STALE' | 'RESTORE_IN_PROGRESS';
export type Failure = { ok: false; code: ErrorCode };
export type Result<T> = { ok: true; value: T } | Failure;
export const failure = (code: ErrorCode): Failure => ({ ok: false, code });

export interface TimeStamp {
  recordedInstant: string;
  zoneId: string;
  utcOffsetMinutes: number;
}
export interface DailyRoutineCheck extends TimeStamp {
  checkId: string;
  itemId: string;
  localDate: string;
  label: string;
  marked: boolean;
  revision: number;
}
export interface ContextToken { datasetId: string; controlEpoch: number }
export interface CheckView { check: DailyRoutineCheck; context: ContextToken }
export interface SetCheckCommand extends TimeStamp {
  operationId: string;
  checkId: string;
  itemId: string;
  localDate: string;
  desiredState: boolean;
  expectedRevision: number;
  context: ContextToken;
}
export interface CommitReceipt {
  operationId: string;
  fingerprint: string;
  check: DailyRoutineCheck;
}
export interface CommitResult { status: 'APPLIED' | 'REPLAYED'; receipt: CommitReceipt }

const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
export const validId = (id: unknown): id is string => typeof id === 'string' && uuidPattern.test(id);
export const natural = (n: unknown): n is number => typeof n === 'number' && Number.isSafeInteger(n) && n >= 0;
export function validLocalDate(value: unknown): value is string {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split('-').map(Number) as [number, number, number];
  const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const days = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return year >= 1 && month >= 1 && month <= 12 && day >= 1 && day <= days[month - 1]!;
}
export function validTime(value: TimeStamp): boolean {
  return typeof value.recordedInstant === 'string'
    && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(value.recordedInstant)
    && Number.isFinite(Date.parse(value.recordedInstant))
    && new Date(value.recordedInstant).toISOString() === value.recordedInstant
    && typeof value.zoneId === 'string' && value.zoneId.length > 0
    && Number.isInteger(value.utcOffsetMinutes) && Math.abs(value.utcOffsetMinutes) <= 840;
}
export function validCheck(check: DailyRoutineCheck): boolean {
  return !!check && validId(check.checkId) && validId(check.itemId)
    && validLocalDate(check.localDate) && typeof check.label === 'string' && check.label.trim().length > 0
    && typeof check.marked === 'boolean' && natural(check.revision) && check.revision > 0 && validTime(check);
}
export function validCommand(command: SetCheckCommand): boolean {
  return !!command && validId(command.operationId) && validId(command.checkId) && validId(command.itemId)
    && validLocalDate(command.localDate) && typeof command.desiredState === 'boolean'
    && natural(command.expectedRevision) && command.expectedRevision > 0
    && !!command.context && validId(command.context.datasetId) && natural(command.context.controlEpoch)
    && validTime(command);
}
export function fingerprint(command: SetCheckCommand): string {
  return JSON.stringify([command.operationId, command.checkId, command.itemId, command.localDate,
    command.desiredState, command.expectedRevision, command.context.datasetId, command.context.controlEpoch,
    command.recordedInstant, command.zoneId, command.utcOffsetMinutes]);
}
export function revise(check: DailyRoutineCheck, command: SetCheckCommand): Result<DailyRoutineCheck> {
  if (!validCommand(command)) return failure('VALIDATION_ERROR');
  if (!validCheck(check)) return failure('CORRUPTION_DETECTED');
  if (check.checkId !== command.checkId || check.itemId !== command.itemId || check.localDate !== command.localDate)
    return failure('NOT_FOUND');
  if (check.revision !== command.expectedRevision) return failure('REVISION_CONFLICT');
  if (!Number.isSafeInteger(check.revision + 1)) return failure('INVARIANT_CONFLICT');
  return { ok: true, value: { ...check, marked: command.desiredState, revision: check.revision + 1,
    recordedInstant: command.recordedInstant, zoneId: command.zoneId, utcOffsetMinutes: command.utcOffsetMinutes } };
}
